import { Close, Download } from "@mui/icons-material";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useState } from "react";
import dayjs from "dayjs";
import { downloadSensorData, getMaxEndDate, getMinStartDate } from "./utils";
import PercentageCircularProgess from "../progress/percentageCircularProgress.component";

const DownloadDialog = ({ handleClose, open, datastreamList, field, modelsData }) => {
  const { t } = useTranslation();

  const token = useSelector((state) => state.auth?.token);

  const allDatastreams = [
    ...(Array.isArray(datastreamList)
      ? datastreamList.map((ds) => ({
          ...ds,
          id_thing: field.id_thing,
          isWeatherStation: false,
        }))
      : []),
  ];

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [progress, setProgress] = useState(0);
  const [openAlert, setOpenAlert] = useState(true);

  const resetDialog = () => {
    setLoading(false);
    setError(null);
    setStartDate(null);
    setEndDate(null);
    setProgress(0);
    setOpenAlert(false);
  };

  const handleCloseDialog = () => {
    resetDialog();
    handleClose();
  };

  const handleDownload = async () => {
    setLoading(true);
    setProgress(0);
    setError(null);
    setOpenAlert(false);

    try {
      await downloadSensorData({
        token,
        allDatastreams,
        startDate,
        endDate,
        fieldLabel: field.label,
        sensors: field.sensors || [],
        modelsData: modelsData || null,
        onProgress: setProgress,
        t,
      });

      resetDialog();
      handleClose();
    } catch (error) {
      console.error("Errore download:", error);
      setError(error.message || t("download_dialog.download_error"));
      setOpenAlert(true);
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  return (
    <Dialog onClose={handleCloseDialog} open={open}>
      <DialogTitle>{t("download_dialog.download_dialog_title")}</DialogTitle>
      <DialogContent>
        <Typography mb={2}>
          {t(
            loading
              ? "download_dialog.download_dialog_content_loading"
              : "download_dialog.download_dialog_content"
          )}
        </Typography>
        {error && (
          <Collapse in={openAlert}>
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setOpenAlert(false);
                  }}
                >
                  <Close fontSize="inherit" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          </Collapse>
        )}
        {loading ? (
          <>
            <PercentageCircularProgess progress={progress} />
            <Typography mb={2}>
              {t("download_dialog.download_dialog_refresh")}
            </Typography>
          </>
        ) : (
          <Stack spacing={2} direction="row">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={t("download_dialog.download_start_date")}
                value={startDate}
                onChange={setStartDate}
                maxDate={endDate ? dayjs(endDate) : undefined}
                minDate={getMinStartDate(endDate)}
                format="DD/MM/YYYY"
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={t("download_dialog.download_end_date")}
                value={endDate}
                onChange={setEndDate}
                minDate={startDate ? dayjs(startDate) : undefined}
                maxDate={getMaxEndDate(startDate)}
                format="DD/MM/YYYY"
              />
            </LocalizationProvider>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<Download />}
          onClick={handleDownload}
          variant="contained"
          color="primary"
          sx={{ textTransform: "none" }}
          disabled={!startDate || !endDate || loading}
        >
          {t("download_dialog.download_button")}
        </Button>
        <Button
          onClick={handleCloseDialog}
          variant="contained"
          color="error"
          sx={{ textTransform: "none" }}
          disabled={loading}
        >
          {t("download_dialog.download_close_button")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DownloadDialog;
